/**
 * THE ROLES INDEX SCREEN, AS ONE ORG-SCOPED READ — every Role in the
 * organisation, and the totals across them.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ONE READ, BECAUSE THE TOTALS ARE NOT DERIVABLE FROM THE ROWS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A client holding the rows could count them, sum the holdings and count the
 * degraded ones. It could NOT compute the organisation's automation ratio: that
 * needs each Role's two hour figures, and a mean of the per-Role percentages is
 * the wrong number (`org-coverage-view.schema.ts` states why, and states what
 * the right weighting is). The same is true of the cumulative impact, which is
 * a sum of money that only some Roles produce.
 *
 * So the totals are served rather than derived, and every one of them is
 * computed from the same population by the same code that produced the rows.
 * Serving the rows and letting the caller combine them is the shape that
 * produced the refusal this endpoint answers.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 EVERY TOTAL CARRIES ITS OWN CENSUS, AND NO ABSENCE IS EVER A ZERO
 * ══════════════════════════════════════════════════════════════════════════
 *
 * An aggregate is where an absent value does the most damage: one Role with no
 * model silently contributes `0`, and the total stays plausible. So no total
 * here is a bare number over an assumed-complete population.
 *
 * - `automation` carries `rolesModelled` / `rolesTotal`.
 * - `cumulativeImpact` carries `rolesProjected` / `rolesTotal`.
 * - `roles`, `holdings` and `needAttention` are counts over rows that all exist,
 *   so they have no census to carry — a Role with no coverage model still has a
 *   name and a holdings count, and those totals are complete by construction.
 *
 * A Role that cannot contribute is EXCLUDED and COUNTED, never summed as zero.
 * The two are different: excluded-and-counted leaves the figure true about a
 * named subset; summed-as-zero leaves it false about everything.
 */
import { z } from "zod";

import { OrgCoverageRatioSchema, OrgCoverageSpreadSchema } from "./org-coverage-view.schema";
import { RoleCoverageMomentumSchema } from "./role-coverage-momentum.schema";
import { CoverageRatioSchema, CoverageSavingsProjectionSchema } from "./role-coverage-view.schema";

/**
 * Why the organisation has no cumulative impact figure.
 *
 * Four arms and no fifth. There is deliberately no `MIXED_CURRENCY`: the
 * currency is one org-level value read once and handed to every Role's
 * evaluation, so two Roles in one organisation cannot disagree about it. An arm
 * for a state the code cannot produce is a diagnostic that reads healthy
 * forever and tells a future reader the opposite of the truth about the schema.
 */
export const ORG_CUMULATIVE_IMPACT_UNAVAILABLE_REASONS = [
  /** No Roles at all — nothing to sum, and nothing wrong. */
  "NO_ROLES",
  /**
   * The organisation never stated a currency, so no Role has a money figure and
   * none can be invented. Not a zero, and not a guessed EUR.
   */
  "NO_CURRENCY",
  /**
   * Roles and a currency exist, but no Role produced a savings projection —
   * each was unavailable for one of the per-Role reasons. The counts travel
   * with it, so `0 of 11` is what a reader sees.
   */
  "NO_PROJECTED_ROLES",
  /**
   * Roles DID project and the MONEY sum is not a finite number. Named
   * separately for the same reason `RATIO_NOT_FINITE` is: a reason that
   * contradicts the count beside it is a payload that disagrees with itself.
   */
  "AMOUNT_NOT_FINITE",
  /**
   * Roles DID project and the HOURS sum is not a finite number.
   *
   * 🚨 A SEPARATE ARM FROM `AMOUNT_NOT_FINITE` ON PURPOSE. One reason covering
   * both sums names the money when the hours are what broke, and sends a reader
   * to audit a rate and a currency over a fault in an hours model. The two sums
   * fail independently, so they are reported independently.
   */
  "HOURS_NOT_FINITE"
] as const;

export type OrgCumulativeImpactUnavailableReason =
  (typeof ORG_CUMULATIVE_IMPACT_UNAVAILABLE_REASONS)[number];

const OrgCumulativeImpactScopeShape = {
  /** Roles that produced a savings projection and were summed. */
  rolesProjected: z.number().int().min(0),
  /** Every Role the organisation holds. */
  rolesTotal: z.number().int().min(0)
};

/**
 * What the organisation's automation is worth in a year, and the hours behind
 * it.
 *
 * SAVINGS-ONLY, at both grains. Revenue never enters this figure — a Role that
 * earns without saving projects `amount: 0`, and that ZERO IS REAL: it means
 * "this Role saves nothing", which is a measurement, and it is a different
 * state from `unavailable`, which means "we could not tell". A client must be
 * able to render `€0` confidently and an em dash honestly, so the two can never
 * be the same value.
 *
 * 🚨 `personHours` IS THE HOURS BEHIND `amount`, NOT THE ORGANISATION'S TOTAL
 * AUTOMATED HOURS. It sums `impactPersonHours` over exactly the Roles that
 * contributed money. Summing it over ALL Roles would put a bigger hours figure
 * beside a smaller money figure on the same card, and the two would silently
 * describe different populations — which is the failure this whole contract is
 * shaped to prevent, appearing inside a single stat.
 */
export const OrgCumulativeImpactSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("projected"),
    /** Major units, savings-only. `0` is a real measurement, not an absence. */
    amount: z.number(),
    /** ISO 4217 alphabetic. One per organisation, so no Role can disagree. */
    currency: z.string(),
    /** Person-hours a year behind `amount` — the same Roles, the same window. */
    personHours: z.number(),
    ...OrgCumulativeImpactScopeShape
  }),
  z.strictObject({
    kind: z.literal("unavailable"),
    reason: z.enum(ORG_CUMULATIVE_IMPACT_UNAVAILABLE_REASONS),
    ...OrgCumulativeImpactScopeShape
  })
]);

/**
 * One Role, as the index screen shows it.
 *
 * `coverage` and `savingsProjection` are the ENGINE's own unions, reused rather
 * than flattened into a percentage and an amount. Flattening is where a
 * `not-modelled` Role becomes `0%` and an `unavailable` impact becomes `€0`,
 * and both of those are lies that render beautifully.
 *
 * `ownerUserId` is the stored fact and nothing more. Resolving it to a display
 * name is a read against a different aggregate; `null` is the honest,
 * complete answer for an unassigned Role and the caller renders it as such.
 */
export const OrgRoleRowSchema = z.strictObject({
  roleId: z.string(),
  name: z.string(),
  /** The Role's short description. `null` when nobody wrote one. */
  jobDescription: z.string().nullable(),
  /** `null` means UNASSIGNED, which is a state, not a missing value. */
  ownerUserId: z.string().nullable(),
  /**
   * What this Role HOLDS, across every table that can hold something. A
   * complete count — every Role has one, and a Role holding nothing is `0`.
   *
   * 🚨 THREE TABLES, NOT ONE. `CountRoleHoldingsByRoleForOrgPort` sums
   * `RoleResource` + `RoleCollectionGrant` + `RoleWorkspaceGrant`. A Collection
   * and a Workspace are shared across Roles, so neither fits `RoleResource`'s
   * `@@unique([organizationId, resourceType, resourceId])` single-owner shape.
   * Counting `RoleResource` alone does not look wrong and is: a Role holding
   * only grants read `0` on this screen until the three-table sum landed.
   *
   * 🚨 IT IS `holdings` AND NOT `systems` BECAUSE THIS MODULE RESERVES "system"
   * FOR THE SIX OWNABLE KINDS. `role-capability.types.ts` spells `resource.view`
   * as *"See the systems (agents, workflows, deployments, templates) in the
   * Role"* and `board.view` as *"a lane holds a mixture of systems, Collection
   * grants and Workspace grants"* — systems CONTRASTED with grants, in the same
   * file. Under the old name this integer was legitimately larger than the
   * number of systems a reader could name, and nothing on screen said so.
   *
   * 🔴 NEVER READ THIS FIELD TO ANSWER "DOES THIS ROLE HOLD A SYSTEM?" It is
   * satisfied by a knowledge-collection grant, so a warning or an empty state
   * gated on `holdings > 0` clears itself the moment somebody grants a
   * collection — which is the very act it would exist to say was not enough.
   * Use the `RoleResource` population (`role.resources`) for that question.
   *
   * ⚠️ AND IT IS NOT THE BOARD'S CARD COUNT EITHER. The Overview draws cards
   * onto SYSTEM-MAP NODES, and the map's nodes come from workflow graphs alone,
   * so a grant nothing points an arrow at is held, counted here, and drawn
   * nowhere. A granted WORKSPACE is worse than that and is undrawable outright:
   * its node key is `workspace:<slug>` (`"workspace.write"` targets
   * `workspaceSlug`) while its card key is `workspace:<uuid>`, and the board
   * joins on that string.
   *
   * Measured on production 2026-08-12: `Finance Operations` in `Proximus
   * Global` — 3 `RoleResource` workflows + 1 `RoleWorkspaceGrant` = 4 here, 3
   * cards on the board, 3 rows on the Resources tab, 1 on the Workspaces tab.
   * Every one of those numbers is correct.
   */
  holdings: z.number().int().min(0),
  /**
   * The health signal, and it is not a guess: `DEGRADED` is the engine's own
   * verdict that at least one model this Role's figures rest on could not be
   * used. `needAttention` in the totals is the count of these.
   */
  integrityStatus: z.enum(["OK", "DEGRADED"]),
  coverage: CoverageRatioSchema,
  /**
   * How `coverage` has MOVED, against the snapshot series behind this Role.
   *
   * 🚨 IT RIDES THIS ROW RATHER THAN A SIXTH ENDPOINT, AND IT COSTS ONE PORT IN
   * AN EXISTING `Promise.all` — no extra round trip and no extra latency. The
   * sibling run-momentum figure sits on `RoleRunHealthRow` instead, and the
   * difference is not arbitrary: run momentum needs a scan over the
   * organisation's whole execution history, which this read's own header
   * refuses to put on the index's first paint. A snapshot read is one
   * index-covered `findMany` bounded by roles × {@link
   * ROLE_COVERAGE_MOMENTUM_WINDOW_DAYS}, cheaper than the three aggregates
   * `holdings` already issues.
   *
   * ⚠️ IT IS NEVER `null` AND NEVER ABSENT. Every Role gets a momentum object;
   * a Role with nothing to compare gets a NAMED state saying which kind of
   * nothing it is. An optional field here would let a client render a blank
   * where a sentence is owed, which is the whole defect this band was built to
   * avoid.
   *
   * 🔴 `momentum.currentRatio` AND `coverage` ARE ONE MEASUREMENT SERVED TWICE
   * and are written from the same evaluation in the same pass — `currentRatio`
   * is `coverage.ratio` when `coverage.kind === "modelled"` and `null`
   * otherwise, both directions. A client should read one or the other and never
   * mix them.
   *
   * There is deliberately NO coverage momentum on `OrgRolesRollupTotals`. The
   * organisation's automation ratio is an hours-weighted figure rather than a
   * mean of the rows (`org-coverage-view.schema.ts` argues why), so an org-level
   * movement would need its own snapshot of the weighted numerator and
   * denominator — a second series, not a sum of this one. `role-too-new` and
   * `not-yet-captured` have no organisation-level meaning either.
   */
  coverageMomentum: RoleCoverageMomentumSchema,
  savingsProjection: CoverageSavingsProjectionSchema
});

/** Every total the index screen's stat band renders. */
export const OrgRolesRollupTotalsSchema = z.strictObject({
  /** Roles in the organisation. Complete by construction. */
  roles: z.number().int().min(0),
  /**
   * Holdings across every Role. Complete by construction.
   *
   * The sum of `OrgRoleRow.holdings`, so it carries that field's whole caveat —
   * read it there before rendering this as a count of systems. It is NOT a
   * count of systems, and it is NOT the Overview board's card total.
   */
  holdings: z.number().int().min(0),
  /** Roles whose coverage integrity is DEGRADED. Complete by construction. */
  needAttention: z.number().int().min(0),
  /** The weighted org-wide ratio, or a named absence. Carries its census. */
  automation: OrgCoverageRatioSchema,
  /** The min–max of the per-Role ratios over the same population. */
  automationSpread: OrgCoverageSpreadSchema,
  /** Savings-only money and the hours behind it. Carries its census. */
  cumulativeImpact: OrgCumulativeImpactSchema
});

/** The Roles index screen, as one org-scoped read. */
export const OrgRolesRollupViewSchema = z.strictObject({
  organizationId: z.string(),
  roles: z.array(OrgRoleRowSchema),
  totals: OrgRolesRollupTotalsSchema
});

/** What the organisation's automation is worth in a year, or a named absence. */
export type OrgCumulativeImpact = z.infer<typeof OrgCumulativeImpactSchema>;

/** One Role, as the index screen shows it. */
export type OrgRoleRow = z.infer<typeof OrgRoleRowSchema>;

/** Every total the index screen's stat band renders. */
export type OrgRolesRollupTotals = z.infer<typeof OrgRolesRollupTotalsSchema>;

/** The Roles index screen, as one org-scoped read. */
export type OrgRolesRollupView = z.infer<typeof OrgRolesRollupViewSchema>;
